import { useEffect, useState } from "react";
import styles from "./Banner.module.css";
import { useCollapse } from "react-collapsed";
import { useTranslation, Trans } from 'react-i18next';

import Donated from "./donated";

const startDate = new Date("2025-08-25T00:00:00Z");
const endDate = new Date("2025-10-14T23:59:59Z");

export default function ProcessingBanner() {
    const { t } = useTranslation();

    const shown = window.localStorage.getItem("donationBannerShown");
    const [isExpanded, setExpanded] = useState(false);
    const { getCollapseProps, getToggleProps } = useCollapse({
        isExpanded,
        duration: 1500,
    });
    const [interval, setInterval] = useState("m");

  const amounts = interval == "m" ? [5, 10, 25, 55] : [5, 10, 25, 55];
  const [amount, setAmount] = useState(10);

    const params = new URLSearchParams();
    params.append("amount", amount);
    params.append("hide_donation_meter", "true");
    params.append("default_interval", interval);
    const origin = window.location.origin;
    params.append("utm_content", JSON.stringify({ origin, shown }));
    params.append("utm_source", window.location.hostname);
    params.append("utm_medium", "banner");
    params.append("utm_campaign", "donation_banner");

    const donorboxURL = `https://donorbox.org/back-to-school-805292?${params.toString()}`;
    const BANNER_ORIGIN = "https://foundation-donate-banner.netlify.app/";

    useEffect(() => {
        window.DonorBox = { widgetLinkClassName: styles.donate };
        if (window.location.hash === "#nobanner") {
            return;
        }
        const hidden = window.localStorage.getItem("donationBannerHidden");

        if (window.location.hash === "#showdonationbanner") {
            show();
            window.localStorage.removeItem("donationBannerHidden");
            return;
        }
        if (window.location.hash === "#previewdonationbanner") {
            show();
            return;
        }

        if (hidden == null) {
            const now = new Date();
            if (now < startDate || now > endDate) {
                return;
            }
            show();
            return;
        }
        const hide = new Date(hidden);
        if (hide > new Date()) {
            return;
        }
        show();
    }, []);

    function show() {
        window.localStorage.setItem(
            "donationBannerShown",
            parseInt(shown ?? "0") + 1,
        );
        setExpanded(true);
    }

    function close() {
        setExpanded(false);
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(4, 0, 0, 0);

        window.localStorage.setItem("donationBannerHidden", tomorrow);
    }
    function dismiss() {
        setExpanded(false);

        const never = new Date(2999, 0, 1);

        window.localStorage.setItem("donationBannerHidden", never);
    }

    function updateAmount(e) {
        // remove anything that is not a number
        const clean = e.target.value.replace(/[^0-9]/g, "");
        e.target.value = `$${clean}`;
        const amount = parseInt(clean);
        if (isNaN(amount)) {
            return;
        }
        console.log(amount);
        setAmount(amount);
    }

    return (
        <div
            className={`${styles.banner}`}
            {...getCollapseProps()}
        >
            <div className={styles.container}>
                <div className={styles.calltoaction}>
                    <div className={styles.calltoaction_inner}>
                        <div className={styles.calltoaction_desktop}>
                            <div className={styles.headerholder}>
                                <img
                                className={styles.logo}
                                src={`${BANNER_ORIGIN}/p5_emote.png`}
                                alt="p5 logo with a smile"
                                />
                                <h1>{t('title')}</h1>
                            </div>
                            <p>
                                <Trans
                                    i18nKey="banner_description.intro_firstParagraph"
                                    components={{ em: <em /> }}
                                />
                            </p>
                            <p>
                                <Trans
                                    i18nKey="banner_description.intro_secondParagraph"
                                    components={{ strong: <strong /> }}
                                />
                            </p>
                            <img
                                className={styles.screenshot}
                                src={`${BANNER_ORIGIN}/contributors_web.jpg`}
                                alt="Compilation of p5.js contributors' Github profile pictures."
                            />
                            <p className={styles.screenshot_caption}>
                                {t('banner_description.contributors_caption')}
                            </p>
                        </div>
                        <div className={styles.calltoaction_mobile}>
                            <div className={styles.headerholder}>
                                <img
                                className={styles.logo}
                                src={`${BANNER_ORIGIN}/p5_emote.png`}
                                alt="p5 logo with a smile"
                                />
                                <h1>{t('title')}</h1>
                            </div>
                            <p>
                                <Trans
                                    i18nKey="banner_description.intro_firstParagraph"
                                    components={{ em: <em /> }}
                                />
                            </p>
                            <p>
                                <Trans
                                    i18nKey="banner_description.intro_secondParagraph"
                                    components={{ strong: <strong /> }}
                                />
                            </p>
                            <img
                                className={styles.screenshot}
                                src={`${BANNER_ORIGIN}/contributors_web.jpg`}
                                alt="Compilation of p5.js contributors' Github profile pictures."
                            />
                            <p className={styles.screenshot_caption}>
                                {t('banner_description.contributors_caption')}
                            </p>
                        </div>
                        <div className={styles.calltoaction_skip_container}>
                            <a
                                className={`${styles.donatemobile} ${styles.mobileonly}`}
                                mobile="true"
                                href={donorboxURL}
                            >
                                {t('buttons.donate')}
                            </a>
                            <a
                                className={styles.calltoaction_skip}
                                onClick={() => dismiss()}
                            >
                                <Donated /> {t('buttons.already_donated')}
                            </a>
                        </div>
                    </div>
                    <div className={styles.calltoaction_arrow} />
                </div>
                <div className={styles.action}>
                    <div className={styles.intervalSelect}>
                        <div
                            className={`${
                                interval == "o" && styles.active
                            } ${styles.interval}`}
                            onClick={() => setInterval("o")}
                        >
                            {t('buttons.one_time')}
                        </div>
                        <div
                            className={`${
                                interval == "m" && styles.active
                            } ${styles.interval}`}
                            onClick={() => setInterval("m")}
                        >
                            <div className={styles.star} /> {t('buttons.monthly')}
                        </div>
                    </div>
                    <div className={styles.amountSelect}>
                        {amounts.map((_amount, index) => (
                            <div
                                className={`${styles.amount} ${
                                    amount == _amount && styles.active
                                }`}
                                key={index}
                                onClick={() => setAmount(_amount)}
                            >
                                ${_amount}
                            </div>
                        ))}
                        <input
                            className={`${styles.amount} ${styles.customamount} ${
                                !amounts.includes(amount) && styles.active
                            }`}
                            type="string"
                            placeholder="Other amount"
                            onClick={updateAmount}
                            onChange={updateAmount}
                        />
                    </div>
                    <a
                        className={styles.donate}
                        href={donorboxURL}
                        target="_blank"
                    >
                        {t('buttons.donate_now')}
                    </a>
                    <div className={styles.moreinfoholder}>
                        <p className={styles.moreinfo}>
                            <Trans
                                i18nKey="more_info.firstParagraph"
                                components={{ strong: <strong /> }}
                            />
                        </p>
                        <p className={styles.moreinfo}>
                            <Trans
                                i18nKey="more_info.secondParagraph"
                                components={{ a: <a href={"https://medium.com/@ProcessingOrg/building-the-private-sketch-feature-what-made-this-journey-so-special-7c4a6b0de8f9"} target="_blank" rel="noreferrer" /> }}
                            />
                        </p>
                    </div>
                </div>
                <div className={styles.close} onClick={close}>
                    <svg
                        className={styles.close_icon}
                        viewBox="0 0 23 23"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M20.4418 2L2.00021 20.4272"
                            stroke="#9CA3AF"
                            stroke-width="3.72285"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M2 2L20.4416 20.4272"
                            stroke="#9CA3AF"
                            stroke-width="3.72285"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </div>
            </div>
            <script
                type="text/javascript"
                defer
                src="https://donorbox.org/install-popup-button.js"
                id="donorbox-popup-button-installer"
            >
            </script>
        </div>
    );
}
